import { useState } from "react";
import { Download, Upload, Loader2, ShieldCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useVault } from "@/context/VaultContext";
import { exportBackup, importBackup, readBackup } from "@/lib/backup";
import { OverwriteConfirm } from "./OverwriteConfirm";
import { downloadBlob, fileStamp, fmtBytes } from "@/lib/util";

function Summary({ data }) {
  const { manifest: m, result: r } = data;
  const rows = [
    ["Notes added / updated", `${r.notesAdded} / ${r.notesUpdated}`], ["Newer notes kept as yours", r.notesKept], ["Versions merged", r.versionsAdded],
    ["Rejected (failed integrity checks)", r.rejected],
    ["Folders added", r.foldersAdded], ["Files added", r.filesAdded],
    ["Files in backup (expected / found)", `${m.fileCount} / ${r.filesFound}`],
    ["Total size (expected / found)", `${fmtBytes(m.totalBytes)} / ${fmtBytes(r.bytesFound)}`],
  ];
  return (
    <div className="rounded-lg border p-4 space-y-2" data-testid="import-summary">
      {r.verified
        ? <p className="flex items-center gap-2 text-verified font-medium" data-testid="import-verified"><ShieldCheck className="h-4 w-4" />Verified — file count, size and hashes match the backup</p>
        : <p className="flex items-center gap-2 text-destructive font-medium" data-testid="import-mismatch"><ShieldAlert className="h-4 w-4" />Verification mismatch — check the backup file</p>}
      {rows.map(([k, v]) => <div key={k} className="flex justify-between text-sm"><span className="text-muted-foreground">{k}</span><span className="font-mono">{v}</span></div>)}
    </div>
  );
}

export default function BackupDialog({ open, onOpenChange }) {
  const { refresh } = useVault();
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const doExport = async () => {
    setBusy("export");
    const { blob, manifest } = await exportBackup();
    downloadBlob(blob, `StarVault_Backup_${fileStamp()}.zip`);
    toast.success("Backup exported", { description: `${manifest.notes} notes • ${manifest.fileCount} files • ${fmtBytes(manifest.totalBytes)}` });
    setBusy("");
  };
  const [pending, setPending] = useState(null);
  const run = async (parsed, overwrite) => {
    setPending(null); setBusy("import");
    try { setResult(await importBackup(parsed, { overwrite })); await refresh(); toast.success("Smart Merge complete"); }
    catch (err) { toast.error("Import failed", { description: err.message }); }
    setBusy("");
  };
  const doImport = async (e) => {
    const f = e.target.files[0]; e.target.value = "";
    if (!f) return;
    setBusy("import");
    try {
      const parsed = await readBackup(f);
      if (parsed.overwrites.length) { setBusy(""); setPending(parsed); } else await run(parsed, false);
    } catch (err) { toast.error("Import failed", { description: err.message }); setBusy(""); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="backup-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Backup & Smart Merge</DialogTitle>
          <DialogDescription>Move your vault between Android and PC without any cloud. Notes merge by UUID; versions are combined, never overwritten.</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <Button onClick={doExport} disabled={!!busy} data-testid="export-backup-button">
            {busy === "export" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}Export Backup
          </Button>
          <label className={`inline-flex items-center justify-center gap-1 rounded-md border px-4 py-2 text-sm font-medium cursor-pointer hover:bg-muted transition-colors ${busy ? "pointer-events-none opacity-50" : ""}`}>
            {busy === "import" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Smart Merge Import
            <input type="file" accept=".zip,application/zip" className="hidden" onChange={doImport} data-testid="import-backup-input" />
          </label>
        </div>
        {result && <Summary data={result} />}
        <OverwriteConfirm pending={pending} onKeep={() => run(pending, false)} onReplace={() => run(pending, true)} onCancel={() => setPending(null)} />
        <p className="text-xs text-muted-foreground">Backups are unencrypted .zip files — store them somewhere safe. The PIN is never included. Imported versions and files are re-checked against their SHA-256 hashes; anything that fails is rejected.</p>
      </DialogContent>
    </Dialog>
  );
}
